/**
 * AllWeatherMembersOnly Component
 * 
 * Displays AWO member data in three sections:
 * 1. Current user's detailed statistics
 * 2. Active members list
 * 3. Inactive members list
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.darkMode - Enable dark mode styling
 * @param {Object} props.meta - Metadata including current user's iid
 */

import React, { useState, useEffect } from "react";
import axios from "axios";

import "../pages/AllWeather.css";

// Category insignia images
import cat1Img from "../images/awo/cat1.jpg";
import cat2Img from "../images/awo/cat2.jpg";
import cat3aImg from "../images/awo/cat3a.jpg";
import cat3bImg from "../images/awo/cat3b.jpg";
import cat3cImg from "../images/awo/cat3c.jpg";

/**
 * Maps category codes to their corresponding insignia images
 */
const categoryImages = {
    cat1: { src: cat1Img, alt: "Category I" },
    cat2: { src: cat2Img, alt: "Category II" },
    cat3a: { src: cat3aImg, alt: "Category IIIa" },
    cat3b: { src: cat3bImg, alt: "Category IIIb" },
    cat3c: { src: cat3cImg, alt: "Category IIIc" }
};

const AllWeatherMembersOnly = (props) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    // Get the data. First check local storage. If not there, get it from the server.
    useEffect(() => {
        const localData = localStorage.getItem("awoMO");

        if (localData) {
            setData(JSON.parse(localData));
            console.log("Data from local storage");
            console.log(JSON.parse(localData));
        } else {
            getData();
            console.log("getting awo data from server");
        }
         return () => {}
    }, []);
    
    const getData = async (obj) => {
      try {
        setLoading(true);
        //const request = "/api/awo_member.php?" + Math.random();
        const { data } = await axios.get("/api/awo_member", {
            params: {
                _t: Date.now(),
            },
        });

        const resObj = data;
        if (resObj) {
            setData(resObj);
            console.log(resObj);
            localStorage.setItem("awoMO", JSON.stringify(resObj));
            setLoading(false);
        } else {
          console.log("User not verified. Send to login page.");
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    /**
     * Refreshes data from the server, clearing localStorage cache
     */
    const handleRefresh = () => {
        localStorage.removeItem("awoMO");
        getData();
    };

    /**
     * Renders category insignia image
     */
    const renderCategoryInsignia = (category) => {
        const imgData = categoryImages[category];
        if (!imgData) return <span className="awo-cat-text">{category || "N/A"}</span>;
        
        return (
            <img
                src={imgData.src}
                alt={imgData.alt}
                className="awo-cat-insignia"
                title={imgData.alt}
            />
        );
    };

    /**
     * Formats a Unix timestamp to a readable date string
     * @param {number} timestamp - Unix timestamp
     * @returns {string} Formatted date like "Jan 3, 2026"
     */
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp * 1000);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };

    const themeClass = props.darkMode ? "dark-theme" : "";

    return (
        <React.Fragment>
            <div className={`awo-page ${themeClass}`}>
                {/* Loading State */}
                {loading && (
                    <div className="awo-loading" role="status" aria-live="polite">
                        <span>Loading AWO Data - Please wait</span>
                    </div>
                )}

                {data && (
                    <main className="awo-content">
                        {/* Header Section */}
                        <header className="awo-member-header">
                            <div className="awo-member-title-section">
                                {data.caller && (
                                    <div className="awo-current-user-header">
                                        {renderCategoryInsignia(data.caller.cat23)}
                                        <h2 className="awo-current-user-name">{data.caller.full_name}</h2>
                                    </div>
                                )}
                            </div>
                            <div className="awo-refresh-container">
                                <div
                                    className="awo-refresh-link"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    aria-label="Refresh data"
                                    title="Refresh data from server"
                                    style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (!loading) handleRefresh();
                                        }
                                    }}
                                >
                                    {loading ? "⟳" : "↻"} Refresh
                                </div>
                            </div>
                        </header>

                        {/* Section 1: Current User Statistics */}
                        {data.caller && (
                            <section className="awo-section awo-user-stats">
                                <h3 className="awo-section-title">Your Statistics</h3>
                                <div className="awo-stats-table-container">
                                    <table className="awo-stats-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>VFR</th>
                                                <th>MVFR</th>
                                                <th>IFR</th>
                                                <th>LIFR</th>
                                                <th>NIFR</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="awo-stats-label">Total</td>
                                                <td className="awo-stats-value">{data.caller.vfr || 0}</td>
                                                <td className="awo-stats-value">{data.caller.mvfr || 0}</td>
                                                <td className="awo-stats-value">{data.caller.ifr || 0}</td>
                                                <td className="awo-stats-value">{data.caller.lifr || 0}</td>
                                                <td className="awo-stats-value">{data.caller.nifr || 0}</td>
                                            </tr>
                                            <tr>
                                                <td className="awo-stats-label">30-Day</td>
                                                <td className="awo-stats-value">{data.caller.vfr30 || 0}</td>
                                                <td className="awo-stats-value">{data.caller.mvfr30 || 0}</td>
                                                <td className="awo-stats-value">{data.caller.ifr30 || 0}</td>
                                                <td className="awo-stats-value">{data.caller.lifr30 || 0}</td>
                                                <td className="awo-stats-value">{data.caller.nifr30 || 0}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                        {/* Section 2: Active Members */}
                        {data.active && data.active.length > 0 && (
                            <section className="awo-section awo-active-members">
                                <h3 className="awo-section-title">Active Members</h3>
                                <div className="awo-members-grid">
                                    {data.active.map((member, index) => (
                                        <div key={member.iid || index} className="awo-member-card">
                                            <div className="awo-member-card-header">
                                                <div className="awo-member-insignia">
                                                    {renderCategoryInsignia(member.cat23)}
                                                </div>
                                                <div className="awo-member-info">
                                                    <h4 className="awo-member-name">{member.full_name}</h4>
                                                    <p className="awo-member-detail">
                                                        Last Flight: {formatDate(member.dlf)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Section 3: Inactive Members */}
                        {data.inactive && data.inactive.length > 0 && (
                            <section className="awo-section awo-inactive-members">
                                <h3 className="awo-section-title">Inactive Members</h3>
                                <div className="awo-inactive-list">
                                    <p>
                                        {data.inactive.map((member, index) => (
                                            <React.Fragment key={member.iid || index}>
                                                {member.full_name}
                                                {index < data.inactive.length - 1 ? "; " : ""}
                                            </React.Fragment>
                                        ))}
                                    </p>
                                </div>
                            </section>
                        )}
                    </main>
                )}
            </div>
        </React.Fragment>
    );
};

export default AllWeatherMembersOnly;
